import { lazy, Suspense, useCallback, useMemo, useState, type PropsWithChildren } from 'react'
import type { ChecklistBreadcrumbSegment } from '../utils/checklistBreadcrumb'
import type { ChecklistWorkflowStatus } from '../types/schema'
import {
  ChecklistDetailPopupContext,
  type ChecklistDetailPopupMode,
  type ChecklistDetailPopupContextValue,
  type OpenChecklistDetailOptions,
  type OpenChecklistDraftOptions,
} from './checklistDetailPopupContextCore'

const ChecklistDetailPopupHost = lazy(() =>
  import('./ChecklistDetailPopupHost').then((module) => ({
    default: module.ChecklistDetailPopupHost,
  })),
)

export const ChecklistDetailPopupProvider = ({ children }: PropsWithChildren) => {
  const [activeChecklistItemId, setActiveChecklistItemId] = useState<string | null>(null)
  const [mode, setMode] = useState<ChecklistDetailPopupMode>('view')
  const [breadcrumb, setBreadcrumb] = useState<ChecklistBreadcrumbSegment[] | null>(null)
  const [initialOccurrenceKey, setInitialOccurrenceKey] = useState<string | null>(null)
  const [pendingStatus, setPendingStatus] = useState<ChecklistWorkflowStatus | null>(null)
  const [activeDraft, setActiveDraft] = useState<OpenChecklistDraftOptions | null>(null)

  const closeChecklistDetail = useCallback(() => {
    setActiveChecklistItemId(null)
    setMode('view')
    setBreadcrumb(null)
    setInitialOccurrenceKey(null)
    setPendingStatus(null)
    setActiveDraft(null)
  }, [])

  const openChecklistDetail = useCallback((
    itemId: string,
    options?: OpenChecklistDetailOptions,
  ) => {
    if (!itemId.trim()) return
    setActiveDraft(null)
    setActiveChecklistItemId(itemId)
    setMode(options?.mode ?? 'view')
    setBreadcrumb(options?.breadcrumb ?? null)
    setInitialOccurrenceKey(options?.initialOccurrenceKey ?? null)
    setPendingStatus(options?.pendingStatus ?? null)
  }, [])

  const openChecklistDraft = useCallback((input: OpenChecklistDraftOptions) => {
    if (!input.workspaceId.trim()) return
    setActiveChecklistItemId(null)
    setMode('edit')
    setBreadcrumb(input.breadcrumb ?? null)
    setInitialOccurrenceKey(null)
    setPendingStatus(null)
    setActiveDraft(input)
  }, [])

  const value = useMemo<ChecklistDetailPopupContextValue>(() => ({
    activeChecklistItemId,
    mode,
    breadcrumb,
    initialOccurrenceKey,
    activeDraft,
    openChecklistDetail,
    openChecklistDraft,
    closeChecklistDetail,
  }), [activeChecklistItemId, activeDraft, breadcrumb, closeChecklistDetail, initialOccurrenceKey, mode, openChecklistDetail, openChecklistDraft])

  return (
    <ChecklistDetailPopupContext.Provider value={value}>
      {children}
      {(activeChecklistItemId || activeDraft) && (
        <Suspense fallback={null}>
          <ChecklistDetailPopupHost
            itemId={activeChecklistItemId}
            mode={mode}
            breadcrumb={breadcrumb}
            initialOccurrenceKey={initialOccurrenceKey}
            pendingStatus={pendingStatus}
            draft={activeDraft}
            onClose={closeChecklistDetail}
            onDraftPersisted={openChecklistDetail}
          />
        </Suspense>
      )}
    </ChecklistDetailPopupContext.Provider>
  )
}
